import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import config from '@config'
import { mobileCSS } from '@measures/responsive'
import Blink from '@components/Blink'

const {
  abTest: { windowKey, localStorageUpdateKey, dashboardPath },
} = config

const getIsLocalStorageExperimentSet = (): boolean => {
  try {
    const featureFlagStorageInfo = (JSON.parse(
      localStorage.getItem(windowKey) as any,
    ) ?? {}) as Record<string, unknown>

    return Object.keys(featureFlagStorageInfo).length > 0
  } catch (err) {
    //! nothing we can do!
    console.error(err)
  }

  return false
}

const StyledFeatureFlagIndicator = styled(Blink)`
  ${({
    theme: {
      colors: { staticRed500 },
    },
  }) => css`
    text-decoration: none;
    width: 40px;
    height: 40px;
    border-radius: 9px;
    font-size: 33px;
    line-height: 42px;
    ${mobileCSS(css`
      width: 34px;
      height: 34px;
      font-size: 23px;
      line-height: 34px;
    `)};
    text-align: center;
    overflow: hidden;
    background-color: ${staticRed500};
    cursor: pointer;

    &:after {
      content: '🧪';
    }
  `}
`

const FeatureFlagIndicator: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const [isShown, setIsShown] = useState<boolean>(false)

  const unsubscribeFnRef = useRef<() => void>(undefined)

  const updateIsShown = useCallback((args: any) => {
    if (
      args.query.queryKey[0] === windowKey &&
      args.query.queryKey[1] === localStorageUpdateKey
    ) {
      setIsShown(getIsLocalStorageExperimentSet())
    }
  }, [])

  useEffect(() => {
    setIsShown(getIsLocalStorageExperimentSet())
    unsubscribeFnRef.current = queryCache.subscribe(updateIsShown)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateIsShown, queryCache])

  return isShown ? <StyledFeatureFlagIndicator href={dashboardPath} /> : null
}

export default FeatureFlagIndicator
